<template>
  <div class="bg-white shadow-sm rounded-lg overflow-hidden">
    <!-- Header -->
    <div class="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
      <h3 class="text-xs font-medium text-gray-700 uppercase tracking-wide">
        Client
      </h3>
      <div v-if="context" class="flex items-center gap-1">
        <a
          :href="`https://customer.onetsolutions.net/administration/clientssummary.php?userid=${context.client.id}`"
          target="_blank"
          class="inline-flex items-center px-1.5 py-0.5 text-xs text-gray-600 hover:text-gray-900 transition-colors"
          title="Fiche client"
        >
          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </a>
        <a
          :href="`https://customer.onetsolutions.net/administration/supporttickets.php?view=any&client=${context.client.id}`"
          target="_blank"
          class="inline-flex items-center px-1.5 py-0.5 text-xs text-gray-600 hover:text-gray-900 transition-colors"
          title="Tickets du client"
        >
          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        </a>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="px-3 py-6 text-center">
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 mx-auto"></div>
      <p class="mt-2 text-xs text-gray-500">Chargement...</p>
    </div>

    <!-- Content -->
    <div v-else-if="context">
      <!-- AI Summary -->
      <div v-if="aiSummary" class="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div class="prose prose-sm max-w-none text-xs" v-html="renderMarkdown(aiSummary)"></div>
      </div>

      <!-- Requestor Info (if different from owner) -->
      <div v-if="context.ticket && context.ticket.requestor_type === 'Authorized User'" class="px-3 py-2 bg-blue-50 border-b border-blue-200">
        <h4 class="text-xs font-medium text-blue-700 mb-2 uppercase tracking-wide">Demandeur</h4>
        <div class="text-xs space-y-1">
          <div class="flex items-center gap-2">
            <svg class="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
            </svg>
            <span class="font-medium text-blue-900">{{ context.ticket.requestor_name }}</span>
            <span class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-300 text-xs">
              Sous-utilisateur
            </span>
          </div>
          <div class="flex items-center gap-2 text-blue-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>{{ context.ticket.requestor_email }}</span>
          </div>
          <div class="mt-2 pt-2 border-t border-blue-200 text-blue-700">
            <div class="flex items-center gap-1">
              <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
              </svg>
              <span class="text-xs">Compte principal: {{ context.ticket.owner_name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Client Info -->
      <div class="px-3 py-2 border-b border-gray-200">
        <h4 class="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Informations</h4>
        <dl class="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
          <div class="sm:col-span-1">
            <dt class="text-xs font-medium text-gray-500">Nom</dt>
            <dd class="mt-0.5 text-xs text-gray-900">{{ context.client.firstname }} {{ context.client.lastname }}</dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-xs font-medium text-gray-500">Email</dt>
            <dd class="mt-0.5 text-xs text-gray-900">{{ context.client.email }}</dd>
          </div>
        </dl>
      </div>

      <!-- Client Stats - Version compacte -->
      <div v-if="context.client_stats" class="px-3 py-1.5 border-b border-gray-200">
        <h4 class="text-xs font-medium text-gray-700 mb-1 uppercase tracking-wide">Stats</h4>
        <dl class="flex flex-wrap gap-1.5 text-xs">
          <div v-if="context.client_stats.incredit" class="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-1.5 py-0.5">
            <dt class="text-xs text-green-700">Crédit:</dt>
            <dd class="font-semibold text-green-900">{{ context.client_stats.creditbalance }}</dd>
          </div>
          <div v-if="context.client_stats.dueinvoicesbalance && context.client_stats.dueinvoicesbalance !== '0.00 €'" class="flex items-center gap-1 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
            <dt class="text-red-600">Impayées:</dt>
            <dd class="text-red-700 font-semibold">{{ context.client_stats.dueinvoicesbalance }}</dd>
          </div>
          <div v-if="context.client_stats.grossRevenue" class="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
            <dt class="text-gray-500">Rev. brut:</dt>
            <dd class="text-gray-900 font-medium">{{ context.client_stats.grossRevenue }}</dd>
          </div>
          <div v-if="context.client_stats.income" class="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
            <dt class="text-gray-500">Rev. net:</dt>
            <dd class="text-gray-900 font-medium">{{ context.client_stats.income }}</dd>
          </div>
        </dl>
      </div>

      <!-- Products -->
      <div v-if="context.products && context.products.length > 0" class="px-3 py-2 border-b border-gray-200">
        <h4 class="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Produits ({{ context.products.length }})</h4>
        <div class="overflow-x-auto">
          <table class="min-w-full text-xs">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-2 py-1 text-left font-medium text-gray-500">Nom / Domaine</th>
                <th class="px-2 py-1 text-left font-medium text-gray-500">Groupe</th>
                <th class="px-2 py-1 text-left font-medium text-gray-500">IP Dédiée</th>
                <th class="px-2 py-1 text-right font-medium text-gray-500">Prix</th>
                <th class="px-2 py-1 text-center font-medium text-gray-500">Statut</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="product in visibleProducts" :key="product.id" class="hover:bg-gray-50">
                <td class="px-2 py-1.5">
                  <a
                    :href="`https://customer.onetsolutions.net/administration/clientsservices.php?userid=${context.client.id}&id=${product.id}`"
                    target="_blank"
                    class="text-gray-900 font-medium hover:text-gray-700 transition-colors inline-flex items-center gap-1"
                  >
                    {{ product.name }}
                    <svg class="h-3 w-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <div class="text-gray-400 text-xs" v-if="product.domain">{{ product.domain }}</div>
                </td>
                <td class="px-2 py-1.5 text-gray-500">{{ product.groupname || '-' }}</td>
                <td class="px-2 py-1.5">
                  <span v-if="product.dedicatedip" class="text-gray-900 font-mono text-xs">{{ product.dedicatedip }}</span>
                  <span v-else class="text-gray-400">-</span>
                </td>
                <td class="px-2 py-1.5 text-right">
                  <div class="text-gray-900 font-medium">{{ product.amount }}€</div>
                  <div class="text-gray-400 text-xs">{{ product.billingcycle }}</div>
                </td>
                <td class="px-2 py-1.5 text-center">
                  <span :class="[
                    'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                    product.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  ]">
                    {{ product.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          v-if="hasMoreProducts && !showAllProducts"
          @click="showAllProducts = true"
          class="mt-2 w-full text-xs text-gray-600 hover:text-gray-800 font-medium py-1 hover:bg-gray-50 rounded transition-colors"
        >
          Voir plus ({{ context.products.length - 5 }})
        </button>
        <button
          v-if="hasMoreProducts && showAllProducts"
          @click="showAllProducts = false"
          class="mt-2 w-full text-xs text-gray-600 hover:text-gray-800 font-medium py-1 hover:bg-gray-50 rounded transition-colors"
        >
          Voir moins
        </button>
      </div>

      <!-- Domains -->
      <div v-if="context.domains && context.domains.length > 0" class="px-3 py-2 border-b border-gray-200">
        <h4 class="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Domaines ({{ context.domains.length }})</h4>
        <div class="overflow-x-auto">
          <table class="min-w-full text-xs">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-2 py-1 text-left font-medium text-gray-500">Domaine</th>
                <th class="px-2 py-1 text-left font-medium text-gray-500">Expiration</th>
                <th class="px-2 py-1 text-center font-medium text-gray-500">Statut</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="domain in visibleDomains" :key="domain.id" class="hover:bg-gray-50">
                <td class="px-2 py-1.5">
                  <a
                    :href="`https://customer.onetsolutions.net/administration/clientsdomains.php?userid=${context.client.id}&id=${domain.id}`"
                    target="_blank"
                    class="text-gray-900 font-medium hover:text-gray-700 transition-colors inline-flex items-center gap-1"
                  >
                    {{ domain.domainname || domain.domain }}
                    <svg class="h-3 w-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </td>
                <td class="px-2 py-1.5 text-gray-500">{{ formatDate(domain.expirydate) }}</td>
                <td class="px-2 py-1.5 text-center">
                  <span :class="[
                    'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                    domain.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  ]">
                    {{ domain.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          v-if="hasMoreDomains && !showAllDomains"
          @click="showAllDomains = true"
          class="mt-2 w-full text-xs text-gray-600 hover:text-gray-800 font-medium py-1 hover:bg-gray-50 rounded transition-colors"
        >
          Voir plus ({{ context.domains.length - 5 }})
        </button>
        <button
          v-if="hasMoreDomains && showAllDomains"
          @click="showAllDomains = false"
          class="mt-2 w-full text-xs text-gray-600 hover:text-gray-800 font-medium py-1 hover:bg-gray-50 rounded transition-colors"
        >
          Voir moins
        </button>
      </div>

      <!-- Invoices -->
      <div v-if="context.recent_invoices && context.recent_invoices.length > 0" class="px-3 py-2 border-b border-gray-200">
        <h4 class="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Factures ({{ context.recent_invoices.length }})</h4>
        <div class="overflow-x-auto">
          <table class="min-w-full text-xs">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-2 py-1 text-left font-medium text-gray-500">N°</th>
                <th class="px-2 py-1 text-left font-medium text-gray-500">Date</th>
                <th class="px-2 py-1 text-right font-medium text-gray-500">Montant</th>
                <th class="px-2 py-1 text-center font-medium text-gray-500">Statut</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="invoice in context.recent_invoices" :key="invoice.id" class="hover:bg-gray-50">
                <td class="px-2 py-1.5">
                  <a
                    :href="`https://customer.onetsolutions.net/administration/billing/invoice/${invoice.id}`"
                    target="_blank"
                    class="text-gray-900 font-medium hover:text-gray-700 transition-colors inline-flex items-center gap-1"
                  >
                    {{ invoice.invoicenum || '#' + invoice.id }}
                    <svg class="h-3 w-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </td>
                <td class="px-2 py-1.5 text-gray-500">{{ formatDate(invoice.date) }}</td>
                <td class="px-2 py-1.5 text-right text-gray-900 font-medium">{{ invoice.total }}€</td>
                <td class="px-2 py-1.5 text-center">
                  <span :class="[
                    'inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium border',
                    invoice.status === 'Paid' ? 'bg-green-50 text-green-700 border-green-200' :
                    invoice.status === 'Unpaid' ? 'bg-red-50 text-red-700 border-red-200' :
                    invoice.status === 'Refunded' ? 'bg-gray-50 text-gray-700 border-gray-200' :
                    'bg-gray-50 text-gray-700 border-gray-200'
                  ]">
                    {{ invoice.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tickets History -->
      <div v-if="context.tickets_history && context.tickets_history.length > 0" class="px-3 py-2">
        <h4 class="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Tickets ({{ context.tickets_history.length }})</h4>
        <div class="overflow-x-auto">
          <table class="min-w-full text-xs">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-2 py-1 text-left font-medium text-gray-500">Sujet</th>
                <th class="px-2 py-1 text-left font-medium text-gray-500">Département</th>
                <th class="px-2 py-1 text-left font-medium text-gray-500">Dernière activité</th>
                <th class="px-2 py-1 text-center font-medium text-gray-500">Statut</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="ticket in visibleTicketsHistory" :key="ticket.id" class="hover:bg-gray-50">
                <td class="px-2 py-1.5">
                  <router-link
                    :to="`/support/tickets/${ticket.id}`"
                    class="text-gray-900 font-medium hover:text-gray-700 transition-colors block"
                  >
                    <div class="max-w-xs truncate">{{ ticket.subject }}</div>
                    <div class="text-gray-400 text-xs">#{{ ticket.tid }}</div>
                  </router-link>
                </td>
                <td class="px-2 py-1.5 text-gray-500">{{ ticket.deptname }}</td>
                <td class="px-2 py-1.5 text-gray-500">{{ formatDate(ticket.lastreply || ticket.date) }}</td>
                <td class="px-2 py-1.5 text-center">
                  <span :class="[
                    'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                    ticket.status === 'Closed' ? 'bg-gray-100 text-gray-800' :
                    ticket.status === 'Open' ? 'bg-orange-100 text-orange-800' :
                    ticket.status === 'Answered' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  ]">
                    {{ ticket.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          v-if="hasMoreTickets && !showAllTickets"
          @click="showAllTickets = true"
          class="mt-2 w-full text-xs text-indigo-600 hover:text-indigo-800 font-medium py-1 hover:bg-indigo-50 rounded transition-colors"
        >
          Voir plus ({{ context.tickets_history.length - 5 }} de plus)
        </button>
        <button
          v-if="hasMoreTickets && showAllTickets"
          @click="showAllTickets = false"
          class="mt-2 w-full text-xs text-gray-600 hover:text-gray-800 font-medium py-1 hover:bg-gray-50 rounded transition-colors"
        >
          Voir moins
        </button>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="px-4 py-8 text-center">
      <svg class="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p class="mt-2 text-sm text-red-600">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { marked } from 'marked';

export default {
  name: 'ContexteClient',
  props: {
    context: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showAllProducts: false,
      showAllDomains: false,
      showAllTickets: false
    };
  },
  computed: {
    aiSummary() {
      if (!this.context) return null;
      // L'API peut retourner ai_summary ou ai_context_summary
      return this.context.ai_summary || this.context.ai_context_summary || null;
    },
    visibleProducts() {
      if (!this.context?.products) return [];
      // Trier par ID décroissant (plus récent en premier)
      const sorted = [...this.context.products].sort((a, b) => b.id - a.id);
      return this.showAllProducts ? sorted : sorted.slice(0, 5);
    },
    hasMoreProducts() {
      return this.context?.products && this.context.products.length > 5;
    },
    visibleDomains() {
      if (!this.context?.domains) return [];
      // Trier par ID décroissant (plus récent en premier)
      const sorted = [...this.context.domains].sort((a, b) => b.id - a.id);
      return this.showAllDomains ? sorted : sorted.slice(0, 5);
    },
    hasMoreDomains() {
      return this.context?.domains && this.context.domains.length > 5;
    },
    visibleTicketsHistory() {
      if (!this.context?.tickets_history) return [];
      // Trier par ID décroissant (plus récent en premier)
      const sorted = [...this.context.tickets_history].sort((a, b) => b.id - a.id);
      return this.showAllTickets ? sorted : sorted.slice(0, 5);
    },
    hasMoreTickets() {
      return this.context?.tickets_history && this.context.tickets_history.length > 5;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = typeof dateString === 'string' && dateString.includes(' ')
          ? parseISO(dateString.replace(' ', 'T'))
          : parseISO(dateString);
        return format(date, 'dd MMM yyyy', { locale: fr });
      } catch (e) {
        return dateString;
      }
    },
    renderMarkdown(text) {
      if (!text) return '';
      return marked(text);
    }
  }
};
</script>

<style scoped>
.prose :deep(h1) { @apply text-lg font-bold mb-2; }
.prose :deep(h2) { @apply text-base font-bold mb-2; }
.prose :deep(h3) { @apply text-sm font-bold mb-1; }
.prose :deep(p) { @apply mb-2; }
.prose :deep(ul) { @apply list-disc pl-5 mb-2; }
.prose :deep(strong) { @apply font-bold; }
</style>
